import { useState, useEffect, useRef, useContext } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import InteractiveElement from '../../../atoms/interactive-element';
import { connect } from 'react-redux';

import PresetCategoryCard from '../../../molecules/screener/preset-cataegory-card';
import styles from './preset-categories.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import screenerAppContext from '../../../../stores/screener/screener-app-context';
import { updatePresetCategory } from '../../../../stores/screener/screener-redux-store';
import FiltersFooter from '../filters-footer';
import PropTypes from 'prop-types';
import Icon from '../../../atoms/icon';

const className = augmentClassName(styles);

export const PresetCategories = ({
  presetCategories,
  slideUp,
  collapsePresetCategory,
  viewResult,
  updatePresets
}) => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [collapsedSections, setCollapsedSections] = useState({});
  const categoryRef = useRef([]);
  const navigationItemsRef = useRef([]);
  const navigationRef = useRef(null);
  const { channel } = useContext(screenerAppContext);

  const scrollTo = id => {
    const yOffset = -50;
    const element = document.getElementById(`category${id}`);
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const toggleCollapse = sectionIndex => {
    setCollapsedSections(prevState => ({
      ...prevState,
      [sectionIndex]: !prevState[sectionIndex]
    }));
  };

  // get the position of each category section to the top of window
  // filter the past section and get the nearest category index
  const getIndexOfCurrentCategoryView = elements => {
    let ranges = elements.map(e => {
      return {
        index: elements.indexOf(e),
        top: e.getBoundingClientRect().top,
        bottom: e.getBoundingClientRect().bottom
      };
    });

    ranges = ranges.filter(r => r.bottom > 50);
    ranges.sort((a, b) => {
      return a.bottom - b.top;
    });
    return ranges[0]?.index;
  };

  // when active category out of scope, scroll left or right to keep tracking
  const getHorizontalScroll = currentCategory => {
    const navigationEl = navigationRef.current.getBoundingClientRect();
    const activeNavigationItem = navigationItemsRef.current[currentCategory];
    const left = activeNavigationItem?.getBoundingClientRect()?.left - navigationEl?.left;
    const width = (navigationEl?.width * 3) / 4;
    return left > width || left <= 0 ? left : 0;
  };

  useEffect(() => {
    if (window.innerWidth < 768) {
      window.onscroll = () => {
        const currentCategory = getIndexOfCurrentCategoryView(categoryRef.current);
        setActiveCategory(currentCategory);
        navigationRef.current.scrollLeft += getHorizontalScroll(currentCategory);
      };
    }
  }, []);

  return (
    <div {...className('placement', 'no-print', slideUp ? 'slide-up' : 'slide-down')} role="form">
      <div {...className('scroll-navigation')} ref={navigationRef}>
        {presetCategories.map(({ sectionLabel }, index) => {
          return (
            <InteractiveElement
              data-key={`${sectionLabel || ''}-${index}`}
              key={`${sectionLabel || ''}-${index}`}
              role="navigation"
              aria-label={sectionLabel}
              tabIndex="0"
              ref={el => {
                navigationItemsRef.current[index] = el;
              }}
              {...className('scroll-item', activeCategory === index && 'clicked')}
              onClick={() => scrollTo(sectionLabel)}
            >
              {sectionLabel}
            </InteractiveElement>
          );
        })}
      </div>
      <Container fluid={'xl'}>
        <Col
          {...className('background')}
          // TIAA Analytics Tracking
          data-analytics-section="notrack"
          // END TIAA Analytics Tracking
        >
          <Col {...className('root')}>
            <Row>
              <Col sm={12}>
                <h2 {...className('title')}>Browse preset categories</h2>
                <p {...className('subtitle')}>Select characteristics to start filtering</p>
              </Col>
            </Row>
            {presetCategories.map(({ sectionLabel, cards }, sectionIndex) => {
              const isCollapsed = collapsedSections[sectionIndex];
              return (
                <div
                  data-key={`${sectionLabel || ''}-${sectionIndex}`}
                  id={sectionIndex}
                  key={`${sectionLabel || ''}-${sectionIndex}`}
                  ref={el => {
                    categoryRef.current[sectionIndex] = el;
                  }}
                >
                  <Row>
                    <Col sm={12}>
                      <h3
                        {...className('bucket-header')}
                        id={`category${sectionLabel}`}
                        onClick={() => toggleCollapse(sectionIndex)}
                        style={{ cursor: 'pointer' }}
                      >
                        {sectionLabel}{' '}
                        <span>
                          {isCollapsed ? (
                            <Icon.ChevronDown {...className('px-2')} height="15px" width="18px" />
                          ) : (
                            <Icon.ChevronUp {...className('px-2')} height="15px" width="18px" />
                          )}
                        </span>
                      </h3>
                    </Col>
                  </Row>
                  {!isCollapsed && (
                    <Row>
                      {cards.map((card, cardIndex) => (
                        <PresetCategoryCard
                          card={card}
                          data-key={`${card.title || ''}-${cardIndex}`}
                          key={`${card.title || ''}-${cardIndex}`}
                          onChange={() => updatePresets(sectionIndex, cardIndex, channel)}
                        />
                      ))}
                    </Row>
                  )}
                </div>
              );
            })}
            <div {...className('empty-space')} />
          </Col>
        </Col>
      </Container>
      <FiltersFooter viewResult={viewResult} collapsePresetCategory={collapsePresetCategory} />
    </div>
  );
};

PresetCategories.propTypes = {
  presetCategories: PropTypes.array,
  slideUp: PropTypes.bool,
  collapsePresetCategory: PropTypes.func,
  viewResult: PropTypes.func,
  updatePresets: PropTypes.func
};

const mapStateToProps = state => {
  return {
    presetCategories: state.filterReducer.filters.presetCategories
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updatePresets: (sectionIndex, cardIndex, channel) =>
      dispatch(updatePresetCategory(sectionIndex, cardIndex, channel))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PresetCategories);
